'use client';

import { useCallback } from 'react';

import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslations } from 'use-intl';

import SurveyAiFormsAiSectionQuestionTitle from './survey-ai-forms-ai-section-question-title';
import { useAiImageChoiceQuestion } from './survey-ai-forms-ai-section-question.hooks';
import type { AISectionQuestionsBaseProps } from './survey-ai-forms-ai-section-questions.types';
import SurveyAiFormsInputComposer from './survey-ai-forms-input-composer';

interface Props extends AISectionQuestionsBaseProps {
  value: string | string[];
  onChange: (value: string | string[]) => void;
  loading?: boolean;
}

export default function SurveyAiFormsAiSectionImageChoiceQuestion({
  question,
  value,
  transcriptValue = '',
  onChange,
  onTranscriptChange,
  onSubmit,
  onNext,
  hasValue = false,
  responseMode,
  isActive = true,
  onFocus,
  registerVoiceStopCallback,
  loading = false,
  isMobile = false,
  isUserVoiceEnabled = false,
}: Props) {
  const t = useTranslations('Usability');

  // Use AI image choice question hook for business logic
  const {
    isListening,
    options,
    selectedValues,
    handleOptionClick,
    handleInputComposerSubmit,
    canSubmit,
  } = useAiImageChoiceQuestion({
    question,
    value,
    transcriptValue,
    onChange,
    onSubmit,
    onNext,
    hasValue,
  });

  // Show max 4 images on desktop, all images on mobile with horizontal scroll
  const displayOptions = options;

  const handleOptionButtonClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const optionValue = e.currentTarget.dataset.optionValue;
      if (optionValue) handleOptionClick(optionValue);
    },
    [handleOptionClick]
  );

  return (
    <div
      data-test='image-choice-question'
      className='flex w-full flex-col gap-6 md:gap-8'
    >
      {/* Question Title */}
      <SurveyAiFormsAiSectionQuestionTitle
        text={question.text}
        assets={question.assets}
      />

      {/* Image Options Grid
          - Wraps automatically when screen is minimized
          - Max 4 per row on desktop, 2 on tablet, 1 on mobile
      */}
      <div
        data-test='image-options-container'
        className='flex w-full flex-wrap gap-2'
      >
        {displayOptions.map((option) => {
          const isSelected = selectedValues.includes(option.value);
          const imageUrl = option.assets?.[0]?.url;

          return (
            <button
              key={option.id}
              type='button'
              onClick={handleOptionButtonClick}
              data-option-value={option.value}
              data-selected={isSelected}
              data-test='image-option'
              className='relative box-border flex h-42.5 min-h-42.5 w-[calc(50%-4px)] cursor-pointer flex-col items-stretch rounded-2xl border border-solid p-2 outline-hidden backdrop-blur-xs backdrop-filter transition-all focus:ring-0 active:scale-[0.98] data-[selected=false]:border-transparent data-[selected=false]:bg-(--brand-bg-unselected) data-[selected=true]:border-(--brand-primary) data-[selected=true]:bg-white data-[selected=true]:p-0 sm:w-45 sm:max-w-45 sm:min-w-45'
            >
              <div
                data-selected={isSelected}
                className='relative shrink-0 overflow-hidden rounded-lg data-[selected=false]:h-28.5 data-[selected=false]:w-full data-[selected=true]:size-full data-[selected=true]:rounded-2xl'
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={option.label}
                    data-selected={isSelected}
                    data-test='image-option-img'
                    className='size-full min-h-28.5 object-contain data-[selected=false]:rounded-lg data-[selected=true]:rounded-2xl'
                  />
                ) : (
                  <div
                    data-selected={isSelected}
                    className='flex size-full min-h-28.5 items-center justify-center bg-gray-300 data-[selected=false]:rounded-lg data-[selected=true]:rounded-2xl'
                  >
                    <span className='[font-family:var(--brand-font)] text-sm text-gray-400'>
                      {t('noImage')}
                    </span>
                  </div>
                )}
              </div>

              {/* Checkbox - Always positioned at bottom center, overlapping image area */}
              <div
                data-selected={isSelected}
                className='absolute top-34.5 left-1/2 z-10 flex size-4 -translate-x-1/2 items-center justify-center transition-all data-[selected=false]:rounded-sm data-[selected=false]:border data-[selected=false]:border-solid data-[selected=false]:border-slate-200 data-[selected=false]:bg-white data-[selected=true]:rounded-sm data-[selected=true]:bg-(--brand-primary)'
              >
                {isSelected && (
                  <FontAwesomeIcon
                    icon={faCheck}
                    className='size-2 text-(--brand-primary-contrast)'
                  />
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Input Composer - for AI transcript input */}
      <SurveyAiFormsInputComposer
        value={transcriptValue}
        onChange={onTranscriptChange}
        onSubmit={handleInputComposerSubmit}
        responseMode={responseMode}
        isActive={isActive}
        onFocus={onFocus}
        registerVoiceStopCallback={registerVoiceStopCallback}
        instanceId={question.id}
        canSubmit={canSubmit}
        isListening={isListening}
        questionType={question.type}
        loading={loading}
        isMobile={isMobile}
        isUserVoiceEnabled={isUserVoiceEnabled}
      />
    </div>
  );
}
